import { useAtom } from "jotai"
import { atomWithStorage } from "jotai/utils"
import { createContext, useState } from "react"

import useIsDesktop from "@hooks/useIsDesktop"

import extractURLSearchParam from "@utils/extractURLSearchParam"
import matchesDesktopScreen from "@utils/matchesDesktopScreen"

import { ComponentNavigation } from "@organisms/index"

import { AppFrame } from "@templates"

import { ROOT_ROUTE } from "@routes/root"

import { AUTH_NEXT_PARAM } from "@constants/searchParams"
import { AUTHENTICATION_METHOD_PREFERENCE } from "@constants/storage"

import styles from "./Auth.module.scss"
import { AuthContextValues, AuthMethod, EmailData, PhoneData } from "./Auth.types"
import AuthAlternativeLoginMethods from "./AuthAlternativeLoginMethods/AuthAlternativeLoginMethods"
import AuthContainer from "./AuthContainer/AuthContainer"
import contentStyles from "./AuthContent/AuthContent.module.scss"
import AuthDisclaimer from "./AuthDisclaimer/AuthDisclaimer"
import AuthDivider from "./AuthDivider/AuthDivider"
import AuthEmailCodeForm from "./AuthEmailCodeForm/AuthEmailCodeForm"
import AuthEmailPasswordForm from "./AuthEmailPasswordForm/AuthEmailPasswordForm"
import AuthHeader from "./AuthHeader/AuthHeader"
import AuthOTP from "./AuthOTP/AuthOTP"
import AuthPhoneCodeForm from "./AuthPhoneCodeForm/AuthPhoneCodeForm"
import AuthPhonePasswordForm from "./AuthPhonePasswordForm/AuthPhonePasswordForm"
import Testimonials from "./Testimonials/Testimonials"

export const AuthContext = createContext<AuthContextValues | undefined>(undefined)
AuthContext.displayName = "AuthContext"

const preferredAuthMethodAtom = atomWithStorage<AuthMethod>(
    AUTHENTICATION_METHOD_PREFERENCE,
    matchesDesktopScreen().isDesktop ? "email_and_code" : "phone_and_code",
    undefined,
    {
        getOnInit: true,
    },
)

export default function Auth() {
    const [preferredAuthMethod, setPreferredAuthMethod] = useAtom(preferredAuthMethodAtom)

    const isDesktop = useIsDesktop()

    const [emailData, setEmailData] = useState<EmailData>({
        email: null,
        emailId: null,
    })
    const [phoneData, setPhoneData] = useState<PhoneData>({
        phone: null,
        phoneId: null,
    })
    const [isWaitingForCodeInput, setIsWaitingForCodeInput] = useState<boolean>(false)

    const urlRouteParam = extractURLSearchParam(AUTH_NEXT_PARAM, false)
    const postAuthRedirectRoute = urlRouteParam || ROOT_ROUTE

    return (
        <AppFrame.Root pageId="Auth" navigationType="none">
            <AppFrame.PageContent>
                <AuthContainer>
                    <div className={styles.authWrapper}>
                        <AuthContext.Provider
                            value={{
                                preferredAuthMethod,
                                setPreferredAuthMethod,
                                emailData,
                                setEmailData,
                                phoneData,
                                setPhoneData,
                                isWaitingForCodeInput,
                                setIsWaitingForCodeInput,
                                postAuthRedirectRoute,
                            }}
                        >
                            <ComponentNavigation.Provider
                                initialComponentId={preferredAuthMethod}
                                className={styles.formContainer}
                                transitionDurationMS={150}
                                persistToHistory={true}
                                historyKey="authMethod"
                            >
                                <div className={styles.formItems}>
                                    <AuthHeader />
                                    <ComponentNavigation.Root className={contentStyles.componentNavigationContent}>
                                        <ComponentNavigation.Component
                                            componentId="email_and_code"
                                            onEnter={() => {
                                                void setPreferredAuthMethod("email_and_code")
                                                setPhoneData({ phone: null, phoneId: null })
                                            }}
                                        >
                                            <ComponentNavigation.Content animationType="slide">
                                                <AuthEmailCodeForm />
                                            </ComponentNavigation.Content>
                                        </ComponentNavigation.Component>
                                        <ComponentNavigation.Component
                                            componentId="email_and_password"
                                            onEnter={() => {
                                                void setPreferredAuthMethod("email_and_password")
                                                setPhoneData({ phone: null, phoneId: null })
                                            }}
                                        >
                                            <ComponentNavigation.Content animationType="slide">
                                                <AuthEmailPasswordForm />
                                            </ComponentNavigation.Content>
                                        </ComponentNavigation.Component>
                                        <ComponentNavigation.Component
                                            componentId="phone_and_code"
                                            onEnter={() => {
                                                void setPreferredAuthMethod("phone_and_code")
                                                setEmailData({ email: null, emailId: null })
                                            }}
                                        >
                                            <ComponentNavigation.Content animationType="slide">
                                                <AuthPhoneCodeForm />
                                            </ComponentNavigation.Content>
                                        </ComponentNavigation.Component>
                                        <ComponentNavigation.Component
                                            componentId="phone_and_password"
                                            onEnter={() => {
                                                void setPreferredAuthMethod("phone_and_password")
                                                setEmailData({ email: null, emailId: null })
                                            }}
                                        >
                                            <ComponentNavigation.Content animationType="slide">
                                                <AuthPhonePasswordForm />
                                            </ComponentNavigation.Content>
                                        </ComponentNavigation.Component>
                                        <ComponentNavigation.Component
                                            componentId="email_and_code_otp"
                                            excludeFromHistory={true}
                                        >
                                            <ComponentNavigation.Content animationType="slide">
                                                <AuthOTP isActive={true} />
                                            </ComponentNavigation.Content>
                                        </ComponentNavigation.Component>
                                        <ComponentNavigation.Component
                                            componentId="phone_and_code_otp"
                                            excludeFromHistory={true}
                                        >
                                            <ComponentNavigation.Content animationType="slide">
                                                <AuthOTP isActive={true} />
                                            </ComponentNavigation.Content>
                                        </ComponentNavigation.Component>
                                    </ComponentNavigation.Root>
                                    <AuthDivider />
                                    <AuthAlternativeLoginMethods />
                                </div>

                                <AuthDisclaimer />
                            </ComponentNavigation.Provider>
                        </AuthContext.Provider>
                        {isDesktop && <Testimonials />}
                    </div>
                </AuthContainer>
            </AppFrame.PageContent>
        </AppFrame.Root>
    )
}
